import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Navigation from "./Navigation.jsx";
import { openCalendly } from "../utils/openCalendly";
import { ReactComponent as Message } from "../assets/message.svg";
import { ReactComponent as Logo } from "../assets/logo.svg";
import { ReactComponent as LogoGrey } from "../assets/logo_grey.svg";
// import { ReactComponent as LogoMobile } from "../assets/logo_mobile.svg"; // Unused for now
import { ReactComponent as UIDO } from "../assets/uido-text.svg";
import { ReactComponent as UIDOLogoFixed } from "../assets/uido_logo_fixed.svg";
import { ReactComponent as MobileMenu } from "../assets/mobile_menu.svg";
import MobileMenuComponent from "./MobileMenuComponent.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { switchLanguageWithFade } from "../utils/languageSwitch";
import { isHomePath } from "../utils/homePath";
import { rememberMobileHeaderHeight } from "../utils/scrollUtils";
// Removed PopupModal import - using badge widget instead
import ButtonWithIcon from "./ButtonWithIcon";

const MINIMAL_MENU_CLOSE_MS = 700;
const MINIMAL_MENU_CLOSE_DELAY_MS = 1440;

const DesktopMenuToggle = ({
  expanded = false,
  interactive = true,
  onClick,
}) => {
  const icon = (
    <>
      <span data-node-id="3001:998" />
      <span data-node-id="3001:999" />
    </>
  );

  if (!interactive) {
    return (
      <span
        className="desktop-menu-toggle desktop is-reference"
        aria-hidden="true"
        data-node-id="3001:997"
      >
        {icon}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="desktop-menu-toggle desktop"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      aria-label={expanded ? "Zwiń menu" : "Rozwiń menu"}
      aria-expanded={expanded}
      data-node-id="3001:997"
    >
      {icon}
    </button>
  );
};

const Header = () => {
  const navDebug = (...args) => {
    if (typeof window === "undefined" || window.__NAV_DEBUG__ !== true) return;
    console.log("[NavDebug]", ...args);
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuAnimating, setIsMenuAnimating] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [showRegularHeader, setShowRegularHeader] = useState(false);
  const [isMinimalMenuClosing, setIsMinimalMenuClosing] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const lastYRef = useRef(0);
  const rafLockRef = useRef(false);
  const suppressRevealRef = useRef(false);
  // Removed isCalendlyOpen state - using badge widget instead
  const [isConsultationOpen, setConsultationOpen] = useState(false);
  const [isConsultationClosing, setConsultationClosing] = useState(false);
  const [isConsultationPressed, setConsultationPressed] = useState(false);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormCtaInView, setIsFormCtaInView] = useState(false);
  const [suppressConsultationStateTransition, setSuppressConsultationStateTransition] =
    useState(false);
  const floatingHeaderRef = useRef(null);
  const engageThresholdRef = useRef(80);
  const compactMobileThresholdRef = useRef(140);
  const mobileMenuBodyLockRef = useRef(null);
  const consultationPressTimeoutRef = useRef(null);
  const minimalMenuCloseTimeoutRef = useRef(null);
  const previousConsultationDisabledRef = useRef(false);
  const modalPresenceRef = useRef(false);

  useEffect(() => {
    return () => {
      if (minimalMenuCloseTimeoutRef.current) {
        window.clearTimeout(minimalMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  const toggleMenu = () => {
    if (isMenuAnimating && !isMenuOpen) {
      navDebug("toggleMenu:blocked-during-closing");
      return;
    }

    navDebug("toggleMenu:start", {
      isMenuOpen,
      isMenuAnimating,
      isMobileViewport: typeof window !== "undefined" ? window.innerWidth < 768 : null,
      scrollY: typeof window !== "undefined" ? window.scrollY : null,
    });
    if (isMenuOpen) {
      // Restore page scroll position when closing mobile menu.
      const lock = mobileMenuBodyLockRef.current;
      if (lock) {
        document.body.style.overflow = lock.overflow || "";
        document.body.style.position = lock.position || "";
        document.body.style.width = lock.width || "";
        document.body.style.top = lock.top || "";
        navDebug("toggleMenu:close:restore-scroll", { restoredScrollY: window.scrollY || 0 });
      }
      // Closing menu
      setIsMenuOpen(false);
      setIsMenuAnimating(true);
      setTimeout(() => {
        setIsMenuAnimating(false);
        mobileMenuBodyLockRef.current = null;
        navDebug("toggleMenu:close:state-updated", { isMenuOpenNext: false, isMenuAnimatingNext: false });
      }, 420); // Match transition duration
    } else {
      // Lock body while mobile menu is open to prevent jump-to-top.
      if (window.innerWidth < 768 && !mobileMenuBodyLockRef.current) {
        rememberMobileHeaderHeight(floatingHeaderRef.current);
        const body = document.body;
        mobileMenuBodyLockRef.current = {
          overflow: body.style.overflow,
          position: body.style.position,
          width: body.style.width,
          top: body.style.top,
        };
        body.style.overflow = "hidden";
        navDebug("toggleMenu:open:lock-scroll", { lockScrollY: window.scrollY || 0 });
      }
      // Opening menu: render hidden state first, then trigger transition to open.
      setIsMenuAnimating(true);
      setIsMenuOpen(false);
      requestAnimationFrame(() => {
        setIsMenuOpen(true);
      });
      setTimeout(() => {
        setIsMenuAnimating(false);
        navDebug("toggleMenu:open:state-updated", { isMenuOpenNext: true, isMenuAnimatingNext: false });
      }, 420);
    }
  };

  useEffect(() => {
    let releaseFrameId = null;
    let settleFrameId = null;

    const cancelModalRelease = () => {
      if (releaseFrameId !== null) {
        window.cancelAnimationFrame(releaseFrameId);
        releaseFrameId = null;
      }
      if (settleFrameId !== null) {
        window.cancelAnimationFrame(settleFrameId);
        settleFrameId = null;
      }
    };

    const detectModalState = () => {
      const hasModal = Boolean(
        document.querySelector(
          '[aria-modal="true"]:not(.mobile-menu-overlay), .project-modal-overlay, .project-modal, .project-detail-modal, .project-detail-modal-overlay, .faq-modal, .faq-modal-overlay, .choice-modal, .choice-modal-overlay, .cookie-modal'
        )
      );

      if (hasModal) {
        cancelModalRelease();
        modalPresenceRef.current = true;
        setIsModalOpen(true);
        return;
      }

      if (!modalPresenceRef.current) return;

      modalPresenceRef.current = false;
      cancelModalRelease();
      // Let modal effect cleanup restore body styles and scroll before revealing the header.
      releaseFrameId = window.requestAnimationFrame(() => {
        releaseFrameId = null;
        settleFrameId = window.requestAnimationFrame(() => {
          settleFrameId = null;
          setIsModalOpen(false);
        });
      });
    };

    detectModalState();
    const observer = new MutationObserver(detectModalState);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-modal"],
    });

    return () => {
      observer.disconnect();
      cancelModalRelease();
    };
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    // If modal opens while mobile menu is open, cleanup menu lock.
    const lock = mobileMenuBodyLockRef.current;
    if (lock) {
      document.body.style.overflow = lock.overflow || "";
      document.body.style.position = lock.position || "";
      document.body.style.width = lock.width || "";
      document.body.style.top = lock.top || "";
      mobileMenuBodyLockRef.current = null;
    }
    setIsMenuOpen(false);
    setIsMenuAnimating(false);
    setShowRegularHeader(false);
    navDebug("modal-open:force-close-menu");
  }, [isModalOpen]);

  useEffect(() => {
    navDebug("menu-visibility", {
      isMenuOpen,
      isMenuAnimating,
      isModalOpen,
      shouldRenderHeader: !isModalOpen && !isMenuOpen && !isMenuAnimating,
      shouldRenderMobileOverlay: !isModalOpen && (isMenuOpen || isMenuAnimating),
    });
  }, [isMenuOpen, isMenuAnimating, isModalOpen]);

  useEffect(() => () => {
    const lock = mobileMenuBodyLockRef.current;
    if (!lock) return;
    document.body.style.overflow = lock.overflow || "";
    document.body.style.position = lock.position || "";
    document.body.style.width = lock.width || "";
    document.body.style.top = lock.top || "";
    mobileMenuBodyLockRef.current = null;
  }, []);

  useEffect(() => () => {
    if (consultationPressTimeoutRef.current) {
      clearTimeout(consultationPressTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const recalculateEngageThreshold = () => {
      const heroElement =
        document.querySelector(".banner") ||
        document.querySelector(".hero") ||
        document.querySelector(".checklist-hero") ||
        document.querySelector(".policy-hero");

      if (!heroElement) {
        engageThresholdRef.current = 80;
        compactMobileThresholdRef.current = 140;
        return;
      }

      const rect = heroElement.getBoundingClientRect();
      const heroBottomByOffset =
        (heroElement.offsetTop || 0) + (heroElement.offsetHeight || 0);
      const heroBottomByRect = (window.scrollY || 0) + rect.bottom;
      const heroBottom = Math.max(heroBottomByOffset, heroBottomByRect);

      engageThresholdRef.current = Math.max(80, Math.round(heroBottom));
      compactMobileThresholdRef.current = engageThresholdRef.current + 120;
    };

    const syncViewport = () => {
      setIsDesktopViewport(mediaQuery.matches);
    };

    syncViewport();
    recalculateEngageThreshold();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(syncViewport);
    }
    window.addEventListener("resize", syncViewport);
    window.addEventListener("resize", recalculateEngageThreshold);

    const onSuppressReveal = (e) => {
      const duration = (e && e.detail && e.detail.duration) || 1200;
      suppressRevealRef.current = true;
      setTimeout(() => {
        suppressRevealRef.current = false;
      }, duration);
    };
    window.addEventListener('header:suppressReveal', onSuppressReveal);

    const handleScroll = () => {
      if (rafLockRef.current) return;
      rafLockRef.current = true;
      requestAnimationFrame(() => {
        const current = window.scrollY || 0;
        const prev = lastYRef.current || 0;
        const delta = current - prev;
        const isMobile = window.innerWidth < 768;
        const showHideThreshold = isMobile ? 12 : 2;
        const engageThreshold = isMobile ? (engageThresholdRef.current || 80) : 0;

        if (current <= engageThreshold) {
          setIsFixed(false);
          setIsScrollingDown(false);
        } else {
          // Once we cross the threshold, keep sticky behavior and switch
          // between compact/full header based on scroll direction.
          setIsFixed(true);

          if (delta <= -showHideThreshold) {
            // scrolling up
            if (!suppressRevealRef.current) {
              setIsScrollingDown(false);
            }
          } else if (delta >= showHideThreshold) {
            // scrolling down
            const shouldShowCompactMenu =
              !isMobile || current > compactMobileThresholdRef.current;
            setIsScrollingDown(shouldShowCompactMenu);
          } else {
            // Ignore tiny trackpad deltas; preserve current compact/full state.
          }
        }

        lastYRef.current = current;
        rafLockRef.current = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", syncViewport);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(syncViewport);
      }
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("resize", recalculateEngageThreshold);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('header:suppressReveal', onSuppressReveal);
    };
  }, []);

  // const buttonOnClickPrimary = () => {}; // Unused for now

  const calendlyClick = openCalendly;

  const clearConsultationPressTimeout = () => {
    if (!consultationPressTimeoutRef.current) return;
    clearTimeout(consultationPressTimeoutRef.current);
    consultationPressTimeoutRef.current = null;
  };

  const releaseConsultationButton = (delay = 0) => {
    clearConsultationPressTimeout();

    if (delay <= 0) {
      setConsultationPressed(false);
      return;
    }

    consultationPressTimeoutRef.current = setTimeout(() => {
      setConsultationPressed(false);
      consultationPressTimeoutRef.current = null;
    }, delay);
  };

  const pressConsultationButton = () => {
    clearConsultationPressTimeout();
    setConsultationPressed(true);
  };

  const handleConsultationButtonClick = (e) => {
    e.stopPropagation();
    pressConsultationButton();
    releaseConsultationButton(330);
    calendlyClick();
  };
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeRoute = isHomePath(location.pathname);

  const shouldRenderHeader = !isModalOpen && !isMenuOpen && !isMenuAnimating;
  const isHeaderConsultationVisible =
    isDesktopViewport &&
    shouldRenderHeader &&
    (!isFixed || !isScrollingDown || showRegularHeader);
  const isHeaderConsultationDisabled = isHomeRoute && isFormCtaInView;
  const consultationButtonClassName = [
    "desktop",
    "header-consultation-button",
    isConsultationPressed ? "is-active" : "",
    isHeaderConsultationDisabled ? "is-figma-disabled" : "",
    suppressConsultationStateTransition ? "is-state-transition-suppressed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const goHome = () => {
    // If already on home page, scroll to top like "Start" button does
    if (isHomeRoute) {
      try { window.dispatchEvent(new CustomEvent('overlay:show', { detail: { duration: 900 } })); } catch(_) {}
      const element = document.querySelector('.home');
      if (element) {
        const rect = element.getBoundingClientRect();
        const pageTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const top = rect.top + pageTop;
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        // Fallback: just scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    // If on different page, navigate to home
    try {
      window.dispatchEvent(new CustomEvent('header:suppressReveal', { detail: { duration: 600 } }));
    } catch (_) {}
    navigate("/");
  };

  const goToForm = () => {
    const formId = "#form";

    if (isHomeRoute) {
      const formElement = document.querySelector(formId);
      if (formElement) {
        const formPosition =
          formElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: formPosition + 200, // Offset 200px below
          behavior: "smooth",
        });
        setOverlayVisible(true);
        setTimeout(() => setOverlayVisible(false), 900);
      }
    } else {
      navigate("/", { replace: true });
      setTimeout(() => {
        const formElement = document.querySelector(formId);
        if (formElement) {
          const formPosition =
            formElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: formPosition + 200, // Offset 200px below
            behavior: "smooth",
          });
          setOverlayVisible(true);
          setTimeout(() => setOverlayVisible(false), 900);
        }
      }, 100);
    }
  };

  const { t, i18n } = useTranslation();

  // Listen for global event to open Calendly popup (used by other components)
  useEffect(() => {
    const onCalendlyOpen = () => {
      calendlyClick();
    };
    window.addEventListener('calendly:open', onCalendlyOpen);
    return () => window.removeEventListener('calendly:open', onCalendlyOpen);
  }, [calendlyClick]);

  useEffect(() => {
    if (!isHomeRoute || typeof window === "undefined") {
      setIsFormCtaInView(false);
      return undefined;
    }

    const buttonSelector =
      ".form-container .form-container__calendar-btn, .form-container__right .form-container__submit-btn";
    const visibilityMap = new Map();
    let observer = null;

    const updateVisibility = () => {
      setIsFormCtaInView(Array.from(visibilityMap.values()).some(Boolean));
    };

    const observeButtons = () => {
      const buttons = Array.from(document.querySelectorAll(buttonSelector));

      visibilityMap.clear();
      observer?.disconnect();

      if (!buttons.length) {
        updateVisibility();
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visibilityMap.set(
              entry.target,
              entry.isIntersecting && entry.intersectionRatio > 0
            );
          });
          updateVisibility();
        },
        {
          threshold: [0, 0.2, 0.4],
        }
      );

      buttons.forEach((button) => {
        visibilityMap.set(button, false);
        observer.observe(button);
      });
    };

    observeButtons();

    const mutationObserver = new MutationObserver(() => {
      observeButtons();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer?.disconnect();
      mutationObserver.disconnect();
      visibilityMap.clear();
      setIsFormCtaInView(false);
    };
  }, [isHomeRoute]);

  useEffect(() => {
    if (previousConsultationDisabledRef.current === isHeaderConsultationDisabled) {
      return;
    }

    if (!isHeaderConsultationVisible) {
      setSuppressConsultationStateTransition(true);
    }

    previousConsultationDisabledRef.current = isHeaderConsultationDisabled;
  }, [isHeaderConsultationDisabled, isHeaderConsultationVisible]);

  useEffect(() => {
    if (!isHeaderConsultationVisible || !suppressConsultationStateTransition) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      setSuppressConsultationStateTransition(false);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isHeaderConsultationVisible, suppressConsultationStateTransition]);

  // Consultation dropdown handlers
  // const toggleConsultation = () => { // Unused for now
  //   setConsultationOpen((prev) => {
  //     if (prev) {
  //       // going from open -> close, trigger closing animation class
  //       setConsultationClosing(true);
  //       setTimeout(() => setConsultationClosing(false), 450);
  //       return false;
  //     }
  //     // open immediately
  //     setConsultationClosing(false);
  //     return true;
  //   });
  // };

  const closeConsultation = () => setConsultationOpen(false);

  useEffect(() => {
    const onClickOutside = (e) => {
      const wrappers = Array.from(document.querySelectorAll('.consultation-wrapper'));
      const clickedInsideAny = wrappers.some((w) => w.contains(e.target));
      if (!clickedInsideAny) {
        if (isConsultationOpen) {
          setConsultationOpen(false);
          setConsultationClosing(true);
          setTimeout(() => setConsultationClosing(false), 450);
        }
      }
    };
    if (isConsultationOpen) {
      document.addEventListener('click', onClickOutside);
    }
    return () => document.removeEventListener('click', onClickOutside);
  }, [isConsultationOpen]);

  const handleOptionConsultation = () => {
    closeConsultation();
    calendlyClick();
  };
  const handleOptionForm = () => {
    closeConsultation();
    goToForm();
  };
  const handleOptionPhone = () => {
    closeConsultation();
    const footer = document.querySelector('[data-section="contact-footer"]');
    setOverlayVisible(true);
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.open(
        'https://wa.me/48516720692?text=Cze%C5%9B%C4%87!%20Chcia%C5%82_bym%20um%C3%B3wi%C4%87%20si%C4%99%20na%20konsultacj%C4%99%20z%20UIDO.',
        '_blank',
        'noopener,noreferrer'
      );
    }
    setTimeout(() => setOverlayVisible(false), 900);
  };
  const handleOptionEmail = () => {
    closeConsultation();
    const footer = document.querySelector('[data-section="contact-footer"]');
    setOverlayVisible(true);
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.open(
        'mailto:kontakt@uido.pl?subject=Konsultacja%20z%20UIDO&body=Cze%C5%9B%C4%87!%20Chcia%C5%82_bym%20um%C3%B3wi%C4%87%20si%C4%99%20na%20konsultacj%C4%99%20z%20UIDO.'
      );
    }
    setTimeout(() => setOverlayVisible(false), 900);
  };

  const changeLanguage = async (lng) => {
    if (isLanguageSwitching) return;

    setIsLanguageSwitching(true);
    try {
      await switchLanguageWithFade({
        lng,
        i18n,
        location,
        navigate,
      });
    } finally {
      setIsLanguageSwitching(false);
    }
  };

  const closeMinimalNav = ({ immediate = false } = {}) => {
    if (isMinimalMenuClosing) return;

    if (minimalMenuCloseTimeoutRef.current) {
      if (!immediate) return;
      window.clearTimeout(minimalMenuCloseTimeoutRef.current);
      minimalMenuCloseTimeoutRef.current = null;
    }

    const startClosing = () => {
      setIsMinimalMenuClosing(true);
      minimalMenuCloseTimeoutRef.current = window.setTimeout(() => {
        setShowRegularHeader(false);
        setIsMinimalMenuClosing(false);
        minimalMenuCloseTimeoutRef.current = null;
      }, MINIMAL_MENU_CLOSE_MS);
    };

    if (immediate) {
      startClosing();
      return;
    }

    minimalMenuCloseTimeoutRef.current = window.setTimeout(
      startClosing,
      MINIMAL_MENU_CLOSE_DELAY_MS
    );
  };

  const handleMinimalNavClick = () => {
    navDebug("minimal-nav:click", { isFixed, isScrollingDown, showRegularHeader });
    if (showRegularHeader) {
      closeMinimalNav({ immediate: true });
      return;
    }

    if (minimalMenuCloseTimeoutRef.current) {
      window.clearTimeout(minimalMenuCloseTimeoutRef.current);
      minimalMenuCloseTimeoutRef.current = null;
    }
    setIsMinimalMenuClosing(false);
    setShowRegularHeader(true);
  };

  // Minimal navigation for scroll down state
  const renderMinimalNavContent = () => (
    <div
      className={`minimal-nav${showRegularHeader ? " is-expanded" : ""}${
        isMinimalMenuClosing ? " is-closing" : ""
      }`}
      onMouseLeave={() => {
        if (isDesktopViewport && showRegularHeader) {
          closeMinimalNav();
        }
      }}
    >
      <div className="minimal-nav-content">
        <div
          className="logo minimal"
          onClick={(e) => {
            e.stopPropagation();
            if (isDesktopViewport) {
              goHome();
              return;
            }
            handleMinimalNavClick();
          }}
          role="button"
          tabIndex={0}
          aria-label="Przejdź do strony głównej"
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            if (isDesktopViewport) {
              goHome();
              return;
            }
            handleMinimalNavClick();
          }}
        >
          <div className="uido-swap minimal" aria-hidden="true">
            <LogoGrey className="uido grey" />
          </div>
          <div className="logo-hover-description">
            <div className="description">
              <span className="top-text">REDESIGN</span>
              <br />
              <span className="bottom-text">SELEKTYWNY</span>
            </div>
          </div>
        </div>
        <div className="minimal-nav-controls mobile">
          <div
            className="message-icon"
            onClick={(e) => {
              e.stopPropagation();
              calendlyClick();
            }}
          >
            <Message />
          </div>
          <button
            type="button"
            className="menu-icon"
            aria-label={t("menu.open")}
            aria-expanded={isMenuOpen}
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu();
            }}
          >
            <MobileMenu />
          </button>
        </div>
        <div className="minimal-menu-right desktop">
          <div className="minimal-menu-items">
            <Navigation onLinkClick={closeMinimalNav} />
            <ButtonWithIcon
              className={`minimal-menu-consultation header-consultation-button${
                isConsultationPressed ? " is-active" : ""
              }${
                isHeaderConsultationDisabled ? " is-figma-disabled" : ""
              }${
                suppressConsultationStateTransition
                  ? " is-state-transition-suppressed"
                  : ""
              }`}
              onClick={(event) => {
                handleConsultationButtonClick(event);
                closeMinimalNav();
              }}
              iconLeft={Message}
              disabled={isHeaderConsultationDisabled}
            >
              {t("cta.free")}
            </ButtonWithIcon>
          </div>
          <DesktopMenuToggle
            expanded={showRegularHeader && !isMinimalMenuClosing}
            onClick={handleMinimalNavClick}
          />
        </div>
      </div>
    </div>
  );

  // Header content component to avoid code duplication
  const HeaderContent = ({ isFixedHeader = false }) => (
    <>
      {isFixedHeader ? (
        <>
          <div className="header-left desktop">
            <div
              className="logo compact"
              onClick={goHome}
              role="button"
              tabIndex={0}
              aria-label="Przejdź do strony głównej"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goHome(); }}
            >
              <div className="logo-icon">
                <Logo />
              </div>
              <UIDO className="uido-text" />
            </div>
            <div className="description">
              <span className="top-text">REDESIGN</span>
              <br />
              SELEKTYWNY
            </div>
          </div>

          <div className="header-left mobile header-left--mobile-fixed">
            <div className="uido-logo-fixed-mobile">
              <UIDOLogoFixed />
            </div>
            <div className="icons">
              <div className="message-icon" onClick={calendlyClick}>
                <Message />
              </div>
              <button
                type="button"
                className="menu-icon"
                onClick={toggleMenu}
                aria-label={t("menu.open")}
                aria-expanded={isMenuOpen}
              >
                <MobileMenu />
              </button>
            </div>
          </div>

          <div className="desktop header-navigation-slot">
            <Navigation />
          </div>

          <div className={`consultation-wrapper${isConsultationOpen ? ' is-open' : ''}${isConsultationClosing ? ' is-closing' : ''}`}>
          <ButtonWithIcon
              className={consultationButtonClassName}
              onClick={handleConsultationButtonClick}
              iconLeft={Message}
              disabled={isHeaderConsultationDisabled}
            >
              {t("cta.free")}
            </ButtonWithIcon>
            <div className="consultation-dropdown" role="menu" aria-label="consultation-options">
              <button type="button" className="consultation-option" onClick={handleOptionConsultation}>
                <span className="option-label">{t("consultation.option.15min")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionForm}>
                <span className="option-label">{t("consultation.option.form")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionPhone}>
                <span className="option-label">{t("consultation.option.phone")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionEmail}>
                <span className="option-label">{t("consultation.option.email")}</span>
              </button>
            </div>
          </div>

          <div className="header-language-menu desktop">
            <DesktopMenuToggle expanded interactive={false} />
          </div>
        </>
      ) : (
        <>
          <div className="header-left desktop">
            <div
              className="logo"
              onClick={goHome}
              role="button"
              tabIndex={0}
              aria-label="Przejdź do strony głównej"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goHome(); }}
            >
              <div className="logo-icon">
                <Logo />
              </div>
              <UIDO className="uido-text" />
            </div>
            <div className="description">
              <span className="top-text">REDESIGN</span>
              <br />
              SELEKTYWNY
            </div>
          </div>

          <div className="header-left mobile header-left--mobile-top">
            <div className="header-left--mobile-top-main">
              <div
                className="logo mobile logo--mobile-top"
                onClick={goHome}
                role="button"
                tabIndex={0}
                aria-label="Przejdź do strony głównej"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goHome(); }}
              >
                <div className="logo-icon">
                  <Logo />
                </div>
                <UIDO className="uido-text" />
              </div>

              <div className="lang">
                <button
                  className={`clickable-button polish ${
                    i18n.language === "pl" ? "active" : ""
                  }`}
                  onClick={() => changeLanguage("pl")}
                  disabled={isLanguageSwitching}
                >
                  PL
                </button>
                <button
                  className={`clickable-button english ${
                    i18n.language === "en" ? "active" : ""
                  }`}
                  onClick={() => changeLanguage("en")}
                  disabled={isLanguageSwitching}
                >
                  EN
                </button>
              </div>
            </div>

            <div className="message-icon" onClick={calendlyClick}>
              <Message />
            </div>
            <button
              type="button"
              className="menu-icon"
              onClick={toggleMenu}
              aria-label={t("menu.open")}
              aria-expanded={isMenuOpen}
            >
              <MobileMenu />
            </button>
          </div>

          <div className="desktop header-navigation-slot">
            <Navigation />
          </div>

          <div className={`consultation-wrapper${isConsultationOpen ? ' is-open' : ''}${isConsultationClosing ? ' is-closing' : ''}`}>
          <ButtonWithIcon
              className={consultationButtonClassName}
              onClick={handleConsultationButtonClick}
              iconLeft={Message}
              disabled={isHeaderConsultationDisabled}
            >
              {t("cta.free")}
            </ButtonWithIcon>
            <div className="consultation-dropdown" role="menu" aria-label="consultation-options">
              <button type="button" className="consultation-option" onClick={handleOptionConsultation}>
                <span className="option-label">{t("consultation.option.15min")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionForm}>
                <span className="option-label">{t("consultation.option.form")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionPhone}>
                <span className="option-label">{t("consultation.option.phone")}</span>
              </button>
              <button type="button" className="consultation-option" onClick={handleOptionEmail}>
                <span className="option-label">{t("consultation.option.email")}</span>
              </button>
            </div>
          </div>

          <div className="header-language-menu desktop">
            <div className="lang">
            <button
              className={`clickable-button polish ${
                i18n.language === "pl" ? "active" : ""
              }`}
              onClick={() => changeLanguage("pl")}
              disabled={isLanguageSwitching}
            >
              PL
            </button>
            <button
              className={`clickable-button english ${
                i18n.language === "en" ? "active" : ""
              }`}
              onClick={() => changeLanguage("en")}
              disabled={isLanguageSwitching}
            >
              EN
            </button>
            </div>
            <DesktopMenuToggle expanded interactive={false} />
          </div>
        </>
      )}
    </>
  );

  const shouldFloatHeader = isDesktopViewport || isFixed || !isHomeRoute;
  const shouldRenderMinimalNav =
    (isFixed && isScrollingDown) || showRegularHeader || isMinimalMenuClosing;
  const mobileHeaderVariantClass = !isDesktopViewport
    ? isFixed
      ? "header--mobile-fixed-state"
      : isHomeRoute
        ? "header--mobile-home-top"
        : "header--mobile-inner-top"
    : "";

  const headerElement = (
    <header
        ref={floatingHeaderRef}
        className={`header ${isHomeRoute ? "header--home" : ""} ${shouldFloatHeader ? "fixed" : "normal"} ${mobileHeaderVariantClass} visible ${shouldFloatHeader && isDesktopViewport ? "desktop-fixed-visible" : ""} ${
          !isFixed ? "top-transparent" : ""
        } ${
          isFixed && isScrollingDown ? "scrolling-down" : ""
        }`}
        style={shouldFloatHeader ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          transform: "translateY(0)",
          transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        } : {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
        }}
      >
        {shouldRenderMinimalNav ? (
          renderMinimalNavContent()
        ) : !isFixed ? (
          <HeaderContent />
        ) : (
          <HeaderContent isFixedHeader />
        )}
      </header>
  );

  return (
    <>
      {/* Always-floating header implementation */}
      {shouldRenderHeader &&
        (typeof document !== "undefined" ? createPortal(headerElement, document.body) : headerElement)}

      {/* Mobile menu - always available regardless of header state */}
      {!isModalOpen && (isMenuOpen || isMenuAnimating) &&
        (typeof document !== "undefined"
          ? createPortal(
              <div className={`header-mobile mobile ${isMenuOpen ? "open" : isMenuAnimating ? "closing" : ""}`}>
                <MobileMenuComponent
                  buttonOnClickPrimary={calendlyClick}
                  toggleMenu={toggleMenu}
                />
              </div>,
              document.body
            )
          : (
              <div className={`header-mobile mobile ${isMenuOpen ? "open" : isMenuAnimating ? "closing" : ""}`}>
                <MobileMenuComponent
                  buttonOnClickPrimary={calendlyClick}
                  toggleMenu={toggleMenu}
                />
              </div>
            ))}

      {/* Calendly badge widget is now loaded in public/index.html */}
      <div className={`page-overlay${isOverlayVisible ? ' is-visible' : ''}`} aria-hidden="true"></div>
    </>
  );
};

export default Header;
